import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { ChevronLeft, Share2, Sparkles } from "lucide-react";
import { supabase, type StyleRequest, type JewelryItem, type ClothingItem, type SelectedItem } from "@/lib/supabase";
import {
  type CatalogEntry,
  STOPWORDS,
  normalize,
  tokenize,
  findCatalogMatch as findCatalogMatchFn,
  cleanItemName as cleanItemNameFn,
  parseNarrative as parseNarrativeFn,
  classifyGarmentRole,
} from "@/lib/style-matching";
import { AppShell } from "@/components/app-shell";
import { ImageBox, useResolvedPhotoUrl } from "@/components/ui-extras";
import { ItemActions } from "@/components/item-actions";
import { useAuth } from "@/hooks/use-auth";
import { WearingTriggerNote } from "@/components/collection-prompts";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { useTryAnother } from "@/lib/use-try-another";
import { getAffiliateLink } from "@/lib/caileve-eye";
import { OrbitBoardSection } from "@/components/requests/OrbitBoardSection";
import { SwapAlternativesStrip } from "@/components/requests/SwapAlternativesStrip";
import { SelectedPiecesList } from "@/components/requests/SelectedPiecesList";
import { InsightCards } from "@/components/requests/InsightCards";
import { DeleteRequestDialog } from "@/components/requests/DeleteRequestDialog";
import { bgFields } from "@/lib/photo-preference";
import { WeatherIndicator } from "@/components/weather-indicator";
import type { WeatherBand, WeatherCondition } from "@/lib/weather";

export const Route = createFileRoute("/requests/$id")({
  component: () => (
    <AppShell>
      <RequestDetail />
    </AppShell>
  ),
});

function RequestDetail() {
  const { id } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const uid = user?.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savingLook, setSavingLook] = useState(false);
  const [gaps, setGaps] = useState<string[]>([]);
  const [gapsLoading, setGapsLoading] = useState(false);
  const gapsSessionKey = `caileve_gaps_checked_${id}`;
  const [gapsChecked, setGapsChecked] = useState(
    () => typeof window !== "undefined"
      ? sessionStorage.getItem(gapsSessionKey) === "1"
      : false
  );
  const [activeSlot, setActiveSlot] = useState<"jewelry" | "clothing" | "accessory" | null>(null);
  const [tappedItem, setTappedItem] = useState<SelectedItem | null>(null);
  const [slotAlternatives, setSlotAlternatives] = useState<Array<{
    id: string;
    name: string;
    photo_url: string | null;
    photo_url_no_bg?: string | null;
    prefer_clean_bg?: boolean | null;
    clean_bg_color?: string | null;
  }>>([]);
  const [swappingSlot, setSwappingSlot] = useState<"jewelry" | "clothing" | "accessory" | null>(null);
  const [hasRefined, setHasRefined] = useState(false);
  const rejectPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showRejectedOverlay, setShowRejectedOverlay] = useState(false);

  const rejectPressHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === "mouse") return;
      rejectPressTimer.current = setTimeout(() => {
        setShowRejectedOverlay(true);
        supabase.from("feedback_log").insert({
          user_id: uid,
          request_id: data?.id ?? null,
          signal: "rejected",
        } as never).then(() => {}, () => {});
        setTimeout(() => setShowRejectedOverlay(false), 2000);
      }, 500);
    },
    onPointerUp: () => { if (rejectPressTimer.current) clearTimeout(rejectPressTimer.current); },
    onPointerCancel: () => { if (rejectPressTimer.current) clearTimeout(rejectPressTimer.current); },
    onPointerLeave: () => { if (rejectPressTimer.current) clearTimeout(rejectPressTimer.current); },
  };

  const refineTooltipKey = `caileve_refine_tooltip_${id}`;
  const [showRefineTooltip, setShowRefineTooltip] = useState(
    () => typeof window !== "undefined"
      ? sessionStorage.getItem(`caileve_refine_tooltip_shown`) !== "1"
      : false
  );
  const { data, isLoading } = useQuery({
    queryKey: ["style_request", id, uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("style_requests")
        .select("*")
        .eq("id", id)
        .eq("user_id", uid!)
        .maybeSingle();
      if (error) throw new Error(error.message || "Network error");
      return (data as StyleRequest | null) ?? null;
    },
  });

  const { data: jewelry } = useQuery({
    queryKey: ["request_jewelry", data?.jewelry_item_id],
    enabled: !!uid && !!data?.jewelry_item_id,
    queryFn: async () => {
      const { data: row } = await supabase
        .from("jewelry_items")
        .select("id, name, photo_url, category, color, material, occasion, is_accessory, feelings")
        .eq("id", data!.jewelry_item_id!)
        .maybeSingle();
      return (row as JewelryItem | null) ?? null;
    },
  });

  const { data: clothing } = useQuery({
    queryKey: ["request_clothing", data?.clothing_item_id],
    enabled: !!uid && !!data?.clothing_item_id,
    queryFn: async () => {
      const { data: row } = await supabase
        .from("clothing_items")
        .select("id, name, photo_url, clothing_type, primary_color, occasion, secondary_color, pattern")
        .eq("id", data!.clothing_item_id!)
        .maybeSingle();
      return (row as ClothingItem | null) ?? null;
    },
  });

  const { data: accessory } = useQuery({
    queryKey: ["request_accessory", data?.accessory_item_id],
    enabled: !!uid && !!data?.accessory_item_id,
    queryFn: async () => {
      const { data: row } = await supabase
        .from("jewelry_items")
        .select("id, name, photo_url, category, color, material, occasion, is_accessory")
        .eq("id", data!.accessory_item_id!)
        .maybeSingle();
      return (row as JewelryItem | null) ?? null;
    },
  });

  // Full catalog for three-state gap matching
  const { data: catalog } = useQuery({
    queryKey: ["request_full_catalog", uid],
    enabled: !!uid,
    queryFn: async () => {
      const [jw, cl] = await Promise.all([
        supabase
          .from("jewelry_items")
          .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color, is_wishlist, is_accessory")
          .eq("user_id", uid!),
        supabase
          .from("clothing_items")
          .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color, is_wishlist")
          .eq("user_id", uid!),
      ]);
      const jewelryRows = (jw.data ?? []) as Array<{
        id: string; name: string | null; photo_url: string | null;
        photo_url_no_bg: string | null; prefer_clean_bg: boolean | null; clean_bg_color: string | null;
        is_wishlist: boolean | null; is_accessory: boolean | null;
      }>;
      const clothingRows = (cl.data ?? []) as Array<{
        id: string; name: string | null; photo_url: string | null; is_wishlist: boolean | null;
        photo_url_no_bg: string | null; prefer_clean_bg: boolean | null; clean_bg_color: string | null;
      }>;
      const out: CatalogEntry[] = [];
      for (const r of jewelryRows) {
        if (!r.name) continue;
        out.push({
          id: r.id,
          name: r.name,
          photo_url: r.photo_url ?? null,
          photo_url_no_bg: r.photo_url_no_bg ?? null,
          prefer_clean_bg: r.prefer_clean_bg ?? null,
          clean_bg_color: r.clean_bg_color ?? null,
          type: r.is_accessory ? "accessory" : "jewelry",
          considering: !!r.is_wishlist,
        });
      }
      for (const r of clothingRows) {
        if (!r.name) continue;
        out.push({
          id: r.id,
          name: r.name,
          photo_url: r.photo_url ?? null,
          photo_url_no_bg: r.photo_url_no_bg ?? null,
          prefer_clean_bg: r.prefer_clean_bg ?? null,
          clean_bg_color: r.clean_bg_color ?? null,
          type: "clothing",
          considering: !!r.is_wishlist,
        });
      }
      return out;
    },
  });

  const matchCatalog = (text: string): CatalogEntry | null =>
    findCatalogMatch(text, catalog ?? []);

  // Resolve the full selected item list — use selected_item_ids when present,
  // fall back to the three FK items for legacy rows.
  // Synthetic ID prefix for the scanned anchor — used to guard tap handling
  const SCANNED_ANCHOR_PREFIX = "scanned_";
  const selectedItems: SelectedItem[] = (() => {
    // When a scanned piece exists, inject it as the anchor at position 0.
    // Uses a synthetic ID — it is not a collection item and is not swappable.
    const scannedAnchor: SelectedItem | null =
      data?.scanned_piece_photo_url
        ? {
            id: `${SCANNED_ANCHOR_PREFIX}${data.id}`,
            type: (data.scanned_piece_category ?? "clothing") as SelectedItem["type"],
            name: data.scanned_piece_description ?? "Photographed piece",
            photo_url: data.scanned_piece_photo_url,
            photo_url_no_bg: null,
            prefer_clean_bg: null,
            clean_bg_color: null,
          }
        : null;
    if (data?.selected_item_ids && Array.isArray(data.selected_item_ids) && data.selected_item_ids.length > 0) {
      // Merge photo_url from FK-resolved items when selected_item_ids has null photos.
      // FK queries (jewelry, clothing, accessory) resolve signed URLs correctly.
      const items = (data.selected_item_ids as SelectedItem[]).map((it) => {
        if (it.photo_url) return it; // already has a photo — use it
        if (it.type === "jewelry" && jewelry?.id === it.id) return { ...it, photo_url: jewelry.photo_url ?? null };
        if (it.type === "clothing" && clothing?.id === it.id) return { ...it, photo_url: clothing.photo_url ?? null };
        if (it.type === "accessory" && accessory?.id === it.id) return { ...it, photo_url: accessory.photo_url ?? null };
        return it;
      });
      return scannedAnchor ? [scannedAnchor, ...items] : items;
    }
    const fb: SelectedItem[] = [];
    if (jewelry) fb.push({ id: jewelry.id, type: "jewelry", name: jewelry.name, photo_url: jewelry.photo_url ?? null, ...bgFields(jewelry) });
    if (clothing) fb.push({ id: clothing.id, type: "clothing", name: clothing.name, photo_url: clothing.photo_url ?? null, ...bgFields(clothing) });
    if (accessory) fb.push({ id: accessory.id, type: "accessory", name: accessory.name, photo_url: accessory.photo_url ?? null, ...bgFields(accessory) });
    return scannedAnchor ? [scannedAnchor, ...fb] : fb;
  })();

  // Build exclusion list from ALL pieces in this look (not just the 3 FK items)
  // plus all pieces from prior attempts in this Try Another chain.
  const [chainExclusionNames, setChainExclusionNames] = useState<string[]>([]);

  useEffect(() => {
    if (!uid || !data) return;

    // Read current look pieces directly from data — never from selectedItems
    // (selectedItems is computed inline on every render, unstable reference)
    const hasAnchors = Array.isArray(data.selected_item_ids) &&
      (data.selected_item_ids as Array<{ is_anchor?: boolean }>).some((i) => i?.is_anchor);

    const currentNames: string[] = Array.isArray(data.selected_item_ids)
      ? hasAnchors
        // Anchor-based look: exclude everything except anchors (existing behavior)
        ? (data.selected_item_ids as Array<{ id?: string; name?: string; is_anchor?: boolean }>)
            .filter((i) => i?.id && !i.id.startsWith("scanned_") && !i.is_anchor)
            .map((i) => i.name)
            .filter((n): n is string => Boolean(n))
        // Full-auto look: exclude all jewelry (deep pool, variation is correct)
        // but limit clothing + accessory exclusions to primary FK items only,
        // preserving catalog availability for the next Try Another call.
        : [
            ...(data.selected_item_ids as Array<{ id?: string; name?: string; type?: string; is_anchor?: boolean }>)
              .filter((i) => i?.id && !i.id.startsWith("scanned_") && i.type === "jewelry")
              .map((i) => i.name)
              .filter((n): n is string => Boolean(n)),
            ...(clothing?.name ? [clothing.name] : []),
            ...(accessory?.name ? [accessory.name] : []),
          ]
      : [
          jewelry?.name ?? null,
          clothing?.name ?? null,
          accessory?.name ?? null,
        ].filter((n): n is string => Boolean(n));

    const collectChain = async () => {
      const allNames = new Set<string>(currentNames);
      let parentId = data.parent_request_id ?? null;
      let depth = 0;
      while (parentId && depth < 5) {
        const { data: parent } = await supabase
          .from("style_requests")
          .select("parent_request_id, selected_item_ids")
          .eq("id", parentId)
          .eq("user_id", uid)
          .maybeSingle();
        if (!parent) break;
        if (Array.isArray(parent.selected_item_ids)) {
          const parentHasAnchors = (parent.selected_item_ids as Array<{ is_anchor?: boolean }>)
            .some((i) => i?.is_anchor);
          (parent.selected_item_ids as Array<{ name?: string; type?: string; is_anchor?: boolean }>).forEach((item) => {
            if (!item?.name || item.is_anchor) return;
            if (parentHasAnchors) {
              allNames.add(item.name);
            } else {
              if (item.type === "jewelry") allNames.add(item.name);
            }
          });
        }
        parentId = parent.parent_request_id ?? null;
        depth++;
      }
      setChainExclusionNames(Array.from(allNames));
    };

    collectChain();
  // Stable dependencies only — data.id, data.parent_request_id, and FK item names.
  // Never include selectedItems or any other inline-computed array.
  }, [uid, data?.id, data?.parent_request_id, jewelry?.name, clothing?.name, accessory?.name]);

  const usedPieceNames = chainExclusionNames.length > 0
    ? chainExclusionNames
    : [jewelry?.name, clothing?.name, accessory?.name].filter((n): n is string => Boolean(n));


  const swapSelectedItem = useCallback(async (
    outgoing: SelectedItem,
    incomingId: string,
    incomingName: string,
    incomingPhotoUrl: string | null
  ) => {
    if (!uid || !data) return;
    setSwappingSlot(outgoing.type);
    try {
      const current: SelectedItem[] = (data.selected_item_ids as SelectedItem[] | null | undefined) ?? selectedItems;
      const updated = current.map((it) =>
        it.id === outgoing.id
          ? { id: incomingId, type: outgoing.type, name: incomingName, photo_url: incomingPhotoUrl }
          : it
      );
      const fkCol = outgoing.type === "jewelry" ? "jewelry_item_id"
        : outgoing.type === "clothing" ? "clothing_item_id"
        : "accessory_item_id";
      await supabase.from("style_requests").update({
        selected_item_ids: updated,
        [fkCol]: incomingId,
      } as never).eq("id", data.id);
      qc.invalidateQueries({ queryKey: ["style_request", id, uid] });
      setHasRefined(true);
      setActiveSlot(null);
      setTappedItem(null);
      setSlotAlternatives([]);
    } catch { /* silent */ }
    finally { setSwappingSlot(null); }
  }, [uid, data, id, qc, selectedItems]);

  const openSlot = useCallback(async (slot: "jewelry" | "clothing" | "accessory") => {
    if (!uid) return;
    if (activeSlot === slot) { setActiveSlot(null); setSlotAlternatives([]); return; }
    setActiveSlot(slot);
    setSlotAlternatives([]);
    if (showRefineTooltip) {
      setShowRefineTooltip(false);
      sessionStorage.setItem("caileve_refine_tooltip_shown", "1");
    }
    try {
      if (slot === "clothing") {
        // Fetch all owned clothing items — we classify roles client-side
        // because clothing_type strings are inconsistent in the database
        const { data: allClothing } = await supabase
          .from("clothing_items")
          .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color, clothing_type")
          .eq("user_id", uid)
          .eq("is_wishlist", false)
          .order("is_favorite", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(60);

        const currentId = data?.clothing_item_id;

        // Classify the current item's role
        const currentItem = (allClothing ?? []).find((c) => c.id === currentId);
        const currentRole = currentItem
          ? classifyGarmentRole(currentItem.clothing_type ?? "", currentItem.name)
          : null;

        // Filter alternatives to the same role, excluding the current item
        const alternatives = (allClothing ?? [])
          .filter((c) => {
            if (c.id === currentId) return false;
            if (!currentRole) return true; // no role detected — show all
            return classifyGarmentRole(c.clothing_type ?? "", c.name) === currentRole;
          })
          .slice(0, 20);

        setSlotAlternatives(alternatives);

      } else {
        // Jewelry or accessory — get the category of the current piece
        const currentId = slot === "jewelry"
          ? data?.jewelry_item_id
          : data?.accessory_item_id;

        let jewelryCategory: string | null = null;
        if (currentId) {
          const { data: cur } = await supabase
            .from("jewelry_items")
            .select("category")
            .eq("id", currentId)
            .single();
          jewelryCategory = cur?.category ?? null;
        }

        let q = supabase
          .from("jewelry_items")
          .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color")
          .eq("user_id", uid)
          .eq("is_wishlist", false)
          .eq("is_accessory", slot === "accessory");

        if (jewelryCategory) q = q.eq("category", jewelryCategory);
        if (currentId) q = q.neq("id", currentId);

        const { data: rows } = await q
          .order("is_favorite", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(20);

        setSlotAlternatives(rows ?? []);
      }
    } catch { setSlotAlternatives([]); }
  }, [uid, data, activeSlot, showRefineTooltip]);

  const swapPiece = useCallback(async (slot: "jewelry" | "clothing" | "accessory", itemId: string) => {
    if (!uid || !data) return;
    setSwappingSlot(slot);
    try {
      const col = slot === "jewelry" ? "jewelry_item_id" : slot === "clothing" ? "clothing_item_id" : "accessory_item_id";
      await supabase.from("style_requests").update({ [col]: itemId } as never).eq("id", data.id);
      qc.invalidateQueries({ queryKey: ["style_request", id, uid] });
      qc.invalidateQueries({ queryKey: [`request_${slot}`, slot === "jewelry" ? data.jewelry_item_id : slot === "clothing" ? data.clothing_item_id : data.accessory_item_id] });
      setHasRefined(true);
      setActiveSlot(null);
      setSlotAlternatives([]);
    } catch { /* silent — piece stays as is */ }
    finally { setSwappingSlot(null); }
  }, [uid, data, id, qc]);

  const handlePieceTap = useCallback((item: SelectedItem) => {
    if (item.id.startsWith(SCANNED_ANCHOR_PREFIX)) return;
    setTappedItem(item);
    openSlot(item.type);
  }, [openSlot]);

  const { onTryAnother, retrying } = useTryAnother(
    data ?? null,
    uid ?? "",
    usedPieceNames
  );

  const del = useMutation({
    mutationFn: async () => {
      if (!uid) return null;
      const { error } = await supabase.from("style_requests").delete().eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Style removed.");
      if (uid) qc.invalidateQueries({ queryKey: ["style_requests", uid] });
      navigate({ to: "/requests" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onShare = async () => {
    if (!data) return;
    const narrative = stripMarkers(data.suggested_look ?? "");
    const text = `${data.request_name || "Style Request"}${data.event ? ` — ${data.event}` : ""}${data.mood ? ` · ${data.mood}` : ""}\n\n${narrative}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: data.request_name || "Style Request", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Suggestion copied to clipboard");
      }
    } catch {
      /* dismissed */
    }
  };

  useEffect(() => {
    if (!data?.suggested_look || gapsChecked || !uid) return;
    setGapsChecked(true);
    sessionStorage.setItem(gapsSessionKey, "1");
    setGapsLoading(true);

    const runGapAnalysis = async () => {
      try {
        const [
          { data: jewelryData },
          { data: clothingData },
          { data: accessoryData },
        ] = await Promise.all([
          supabase
            .from("jewelry_items")
            .select("name, category, color, material")
            .eq("user_id", uid)
            .eq("is_wishlist", false)
            .eq("is_accessory", false)
            .limit(10),
          supabase
            .from("clothing_items")
            .select("name, clothing_type, primary_color")
            .eq("user_id", uid)
            .eq("is_wishlist", false)
            .limit(10),
          supabase
            .from("jewelry_items")
            .select("name, category, accessory_type")
            .eq("user_id", uid)
            .eq("is_wishlist", false)
            .eq("is_accessory", true)
            .limit(10),
        ]);

        const catalogSummary = [
          ...(jewelryData ?? []).map((i) =>
            [i.name, i.category, i.color, i.material].filter(Boolean).join(", ")
          ),
          ...(clothingData ?? []).map((i) =>
            [i.name, i.clothing_type, i.primary_color].filter(Boolean).join(", ")
          ),
          ...(accessoryData ?? []).map((i) =>
            [i.name, i.category, i.accessory_type].filter(Boolean).join(", ")
          ),
        ].join("\n");

        const { data: fnData } = await supabase.functions.invoke(
          "style-me-gaps",
          {
            body: {
              catalog_summary: catalogSummary,
              already_selected: selectedItems.map((i) => `${i.name} [${i.type}]`),
            },
          }
        );

        if (fnData && !fnData.complete && Array.isArray(fnData.gaps)) {
          setGaps(fnData.gaps.filter((g: string) => g.trim().length > 0));
        }
      } catch {
        // Silent — gaps section simply doesn't show
      } finally {
        setGapsLoading(false);
      }
    };

    runGapAnalysis();
  }, [data?.suggested_look, data?.scanned_piece_description, gapsChecked, uid]);

  const onSaveAsLook = async () => {
    if (!data || !uid || savingLook) return;
    setSavingLook(true);
    try {
      const lookName =
        data.request_name ||
        [data.event, data.season].filter(Boolean).join(" — ") ||
        "My Look";

      const { data: look, error } = await supabase
        .from("saved_looks")
        .insert({
          user_id: uid,
          name: lookName,
          occasion: data.event || null,
          rating: 5,
          styling_notes: data.suggested_look
            ? stripMarkers(data.suggested_look).slice(0, 2000)
            : null,
          mood: data.mood || null,
          feelings: data.want_to_feel || null,
          look_photo_url:
            data.scanned_piece_photo_url ??
            clothing?.photo_url ??
            jewelry?.photo_url ??
            null,
        } as never)
        .select("id")
        .single();

      if (error) throw new Error(error.message || "Network error");
      const lookId = (look as { id: string }).id;

      const rows: Array<Record<string, unknown>> = selectedItems
        .filter((it) => !it.id.startsWith(SCANNED_ANCHOR_PREFIX))
        .map((it, idx) => ({
          look_id: lookId,
          item_type: it.type,
          item_id: it.id,
          position: idx,
          clothing_item_id: it.type === "clothing" ? it.id : null,
          jewelry_item_id: it.type !== "clothing" ? it.id : null,
        }));
      if (rows.length) {
        await supabase.from("saved_look_items").insert(rows as never);
      }

      toast.success("Your look is saved. This is exactly what your collection is for.");
      void (async () => {
        try {
          const today = new Date().toISOString().split("T")[0];
          const wearRows = selectedItems
            .filter((it) => !it.id.startsWith(SCANNED_ANCHOR_PREFIX))
            .map((it) => ({
              user_id: uid,
              item_id: it.id,
              item_type: it.type === "clothing" ? "clothing" : it.type === "accessory" ? "accessory" : "jewelry",
              worn_at: today,
              logged_via: "daily_look",
            }));
          if (wearRows.length > 0) {
            await supabase.from("wear_logs").insert(wearRows as never);
          }
        } catch {
          // Silent — wear logging must never affect the save flow
        }
      })();
      track("look_saved");
      supabase.from("feedback_log").insert({
        user_id: uid,
        request_id: data.id,
        signal: "saved",
      } as never).then(() => {}, () => {});
      navigate({ to: "/looks/$id", params: { id: lookId } });
    } catch {
      toast.error("Couldn't save this look. Please try again.");
    } finally {
      setSavingLook(false);
    }
  };

  if (authLoading || !uid || isLoading) {
    return <p className="max-w-3xl mx-auto px-8 py-16 text-[var(--caileve-lilac-text)]">Loading…</p>;
  }
  if (!data) {
    return (
      <p className="max-w-3xl mx-auto px-8 py-16 font-serif-display text-[var(--caileve-ink)]">
        Request not found.
      </p>
    );
  }

  const parsed = parseNarrative(data.suggested_look ?? "");
  const hasSuggestion = !!parsed.recommendation || !!parsed.complement ||
    !!parsed.searchTerms || !!parsed.findThese;
  const styleGoals = [
    { label: "Event", value: data.event },
    { label: "Season", value: data.season },
    { label: "Time of day", value: data.time_of_day },
    {
      label: "Conditions",
      value: (() => {
        const wc = data.weather_context as {
          summary?: string;
          band?: WeatherBand;
          condition?: WeatherCondition;
          source?: string;
        } | null;
        if (!wc || wc.source === "unavailable") return null;
        const summary = wc.summary ?? null;
        if (!summary) return null;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <WeatherIndicator condition={wc.condition ?? null} band={wc.band ?? null} />
            <span>{summary}</span>
          </span>
        );
      })(),
    },
    { label: "Mood", value: data.mood },
  ].filter((p) => p.value != null && (typeof p.value !== "string" || p.value.trim().length > 0));
  const wantToFeel = (data.want_to_feel ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const hasMoodboard = !!(
    clothing ||
    jewelry ||
    accessory ||
    data.scanned_piece_photo_url ||
    data.scanned_piece_cutout_url
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" style={{ color: "var(--caileve-ink)" }}>
      <div className="flex items-center justify-between mb-8">
        <Link
          to="/requests"
          className="inline-flex items-center text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-ink)]"
        >
          <ChevronLeft className="h-3 w-3 mr-1" /> All requests
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Share suggestion"
            onClick={onShare}
            className="h-9 w-9 flex items-center justify-center rounded-full border border-[var(--caileve-soft)] text-[var(--caileve-lilac)] hover:bg-[var(--caileve-soft)]"
          >
            <Share2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <ItemActions onDelete={() => setConfirmDelete(true)} />
        </div>
      </div>

      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-muted)]">Style Verdict</p>
        <h1
          className="font-serif-display text-4xl md:text-5xl mt-3 leading-[1.05]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          {...rejectPressHandlers}
        >
          {data.request_name || "Your Look"}
        </h1>
        {showRejectedOverlay && (
          <p
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "14px",
              color: "var(--caileve-muted)",
              textAlign: "center",
              transition: "opacity 0.3s ease",
            }}
          >
            Got it. Cailève will try something different.
          </p>
        )}
        {(() => {
          const parts = [
            data.event,
            data.season,
            data.time_of_day,
            data.mood,
            ...(data.want_to_feel
              ? (data.want_to_feel as string).split(',').map((s: string) => s.trim()).filter(Boolean)
              : []),
          ].filter((p): p is string => !!p && p.trim().length > 0);
          if (parts.length === 0) return null;
          return (
            <p
              className="mt-2 italic"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: '1.1rem',
                color: 'var(--caileve-lilac)',
                letterSpacing: '0.01em',
              }}
            >
              {parts.join(' · ')}
            </p>
          );
        })()}
      </header>

      <OrbitBoardSection
        selectedItems={selectedItems}
        requestName={data.request_name || ""}
        scannedAnchorPrefix={SCANNED_ANCHOR_PREFIX}
        onItemTap={handlePieceTap}
      />

      <SwapAlternativesStrip
        activeSlot={activeSlot}
        tappedItem={tappedItem}
        slotAlternatives={slotAlternatives}
        swappingSlot={swappingSlot}
        onSelect={(alt) => swapSelectedItem(tappedItem!, alt.id, alt.name, alt.photo_url)}
      />

      {showRefineTooltip && selectedItems.length > 0 && selectedItems.some((it) => !!it.photo_url) && (
        <p
          className="mt-3 text-center"
          style={{
            fontSize: 11,
            color: "var(--caileve-muted)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic",
            letterSpacing: "0.05em",
          }}
        >
          Tap any piece to refine it.
        </p>
      )}

      {hasRefined && (
        <p
          className="mt-3 text-center"
          style={{
            fontSize: 11,
            color: "var(--caileve-muted)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic",
          }}
        >
          You've refined this look. The styling notes reflect the original composition.
        </p>
      )}

      <SelectedPiecesList
        selectedItems={selectedItems}
        tappedItem={tappedItem}
        scannedAnchorPrefix={SCANNED_ANCHOR_PREFIX}
        onPieceTap={handlePieceTap}
        cleanItemName={cleanItemName}
      />

      {data.scanned_piece_description && !clothing && !jewelry && (
        <p style={{
          fontSize: 11,
          color: "var(--caileve-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          marginTop: 16,
        }}>
          Styled around: {data.scanned_piece_description}
        </p>
      )}

      {styleGoals.length > 0 && (
        <section className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-muted)]">Styling Goal</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-5">
            {styleGoals.map((p) => (
              <Pair key={p.label} label={p.label} value={p.value} />
            ))}
          </div>
          {(() => {
            const wc = data.weather_context as { summary?: string; source?: string } | null;
            if (!wc || wc.source === "unavailable") return null;
            return (
              <p style={{ fontSize: 10, color: "var(--caileve-muted)", marginTop: 8 }}>
                Weather by OpenWeather
              </p>
            );
          })()}
        </section>
      )}

      {wantToFeel.length > 0 && (
        <section className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-muted)]">
            How you want to feel
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {wantToFeel.map((f) => (
              <span
                key={f}
                className="px-3 py-1 rounded-full text-sm"
                style={{
                  backgroundColor: "var(--caileve-copper)",
                  color: "var(--caileve-bg)",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                }}
              >
                {f}
              </span>
            ))}
          </div>
        </section>
      )}

      {!hasSuggestion && (
        <section className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-muted)]">Suggested Look</p>
          <div
            className="mt-4 rounded-sm px-6 py-10 text-center"
            style={{ backgroundColor: "var(--caileve-soft)" }}
          >
            <p
              className="text-xl italic"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-lilac)" }}
            >
              Your styling recommendation will appear here.
            </p>
          </div>
        </section>
      )}

      <InsightCards parsed={parsed} />

      {(gapsLoading || gaps.length > 0) && (
        <section className="mt-8">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-muted)]">
            What Else You Need
          </p>
          {gapsLoading ? (
            <p
              className="mt-4 text-sm italic"
              style={{
                color: "var(--caileve-muted)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              Checking your collection…
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-3 mt-4">
                {gaps.map((gap, i) => (
                  <GapRow key={i} text={gap} matcher={matchCatalog} />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* findThese now rendered inside unified pieces section above */}

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onSaveAsLook}
          disabled={savingLook}
          className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-[var(--caileve-gold)] text-[var(--caileve-bg)] text-sm uppercase tracking-[0.25em] hover:brightness-110 transition"
        >
          {savingLook ? (
            "Saving…"
          ) : (
            <>
              <Sparkles className="h-4 w-4" /> Save as Look
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onShare}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--caileve-lilac)] text-[var(--caileve-lilac-text)] text-xs uppercase tracking-[0.25em] hover:bg-[var(--caileve-soft)]"
        >
          <Share2 className="h-4 w-4" /> Share
        </button>
        {(data.attempt ?? 1) < 4 && (
          <button
            type="button"
            onClick={onTryAnother}
            disabled={retrying}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--caileve-lilac)] text-[var(--caileve-lilac-text)] text-xs uppercase tracking-[0.25em] hover:bg-[var(--caileve-soft)] disabled:opacity-60"
          >
            {retrying ? "Finding another look…" : "Try another combination"}
          </button>
        )}
      </div>

      <WearingTriggerNote />

      <DeleteRequestDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        requestName={data.request_name || "Untitled Request"}
        isPending={del.isPending}
        onConfirm={() => del.mutate()}
      />
    </div>
  );
}

function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-muted)]">{label}</p>
      <p
        className="text-xl mt-2 text-[var(--caileve-ink)]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {value || "—"}
      </p>
    </div>
  );
}

function stripMarkers(s: string) {
  return s.replace(/\*\*(.+?)\*\*/g, "").trim();
}

// Imported from @/lib/style-matching — local aliases preserve all call sites
const findCatalogMatch = findCatalogMatchFn;
const cleanItemName = cleanItemNameFn;

function GapRow({
  text,
  matcher,
}: {
  text: string;
  matcher: (text: string) => CatalogEntry | null;
}) {
  const match = matcher(text);
  const { url: photoUrl } = useResolvedPhotoUrl(match?.photo_url ?? null);

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    backgroundColor: "var(--caileve-bg)",
    border: "1px solid var(--caileve-soft)",
    borderRadius: 4,
    textDecoration: "none",
    color: "var(--caileve-ink)",
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: 16,
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    color: match ? "var(--caileve-lilac-text)" : "var(--caileve-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    flexShrink: 0,
    marginLeft: "auto",
  };

  const thumb =
    match && photoUrl ? (
      <img
        src={photoUrl}
        alt={match.name}
        style={{
          width: 44,
          height: 44,
          objectFit: "cover",
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
    ) : match ? (
      <div
        style={{
          width: 44,
          height: 44,
          backgroundColor: "var(--caileve-soft)",
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
    ) : null;

  const label = match
    ? match.considering
      ? "In Your Edit"
      : "In Your Collection"
    : "Find this";

  const inner = (
    <>
      {thumb}
      <span style={{ flex: 1, minWidth: 0 }}>
        {match ? match.name : text}
      </span>
      <span style={labelStyle}>{label}</span>
    </>
  );

  if (match) {
    if (match.type === "jewelry") {
      return (
        <Link to="/jewelry/$id" params={{ id: match.id }} style={rowStyle}>
          {inner}
        </Link>
      );
    }
    if (match.type === "clothing") {
      return (
        <Link to="/clothing/$id" params={{ id: match.id }} style={rowStyle}>
          {inner}
        </Link>
      );
    }
    return (
      <Link to="/accessories/$id" params={{ id: match.id }} style={rowStyle}>
        {inner}
      </Link>
    );
  }

  const affiliate = getAffiliateLink(text);
  return (
    <a
      href={affiliate ? affiliate.url : `https://www.google.com/search?q=${encodeURIComponent(text)}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ ...rowStyle, borderStyle: "dashed" }}
    >
      {inner}
    </a>
  );
}

const parseNarrative = parseNarrativeFn;
